import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, RotateCcw, Search, ShieldAlert, SlidersHorizontal, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SearchPagination from '../components/SearchPagination';
import SearchCountLabel from '../components/SearchCountLabel';
import { SearchCount } from '../api/searchCount';
import { useSearchCount } from '../hooks/useSearchCount';
import { isSearchCursorError, releaseSearchSession, useSearchCursor } from '../hooks/useSearchCursor';
import SearchProgress from '../components/SearchProgress';
import EnstruCategoryPicker, { PickedCategory, PickedCode } from '../components/EnstruCategoryPicker';
import EnstruCategoryButton from '../components/EnstruCategoryButton';
import SupplierCompanySearch from '../components/SupplierCompanySearch';
import { getLotRefs, LotRefsResponse, fmtDate } from '../api/lots';
import { asCodeList, searchSuppliers, SupplierListItem } from '../api/suppliers';
import { analytics } from '../analytics';
import { categoryGroup, categorySelectionParams, readCategorySelection } from '../utils/categorySelection';
import ProcurementAccessGate from '../components/ProcurementAccessGate';
import ProcurementPreview from '../components/ProcurementPreview';
import ProcurementTrialPanel from '../components/ProcurementTrialPanel';
import { useProcurementAccess } from '../context/ProcurementAccessContext';

const PAGE_SIZE = 20;

export default function SuppliersPage() {
  return <ProcurementAccessGate feature="suppliers" fallback={<ProcurementPreview kind="suppliers" />}>
    <SuppliersContent />
  </ProcurementAccessGate>;
}
function SuppliersContent() {
  const { t, language } = useLanguage();
  const { access } = useProcurementAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cursorForPage, rememberPage } = useSearchCursor(searchParams);

  const [items, setItems] = useState<SupplierListItem[]>([]);
  const [countSource, setCountSource] = useState<SearchCount | null>(null);
  const count = useSearchCount(countSource);
  const total = count.total;
  const [hasMore, setHasMore] = useState(false);
  const [cursorMode, setCursorMode] = useState(false);
  const [cursorExpired, setCursorExpired] = useState(false);
  const [sortDegraded, setSortDegraded] = useState(false);
  const [categoryPartial, setCategoryPartial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchFailed, setSearchFailed] = useState(false);
  const [refs, setRefs] = useState<LotRefsResponse | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const q = searchParams.get('q') || '';
  const page = Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0);
  const cursor = cursorForPage(page);
  const enstruCode = searchParams.get('enstru') || '';
  const kpvedSection = searchParams.get('section') || '';
  const catGroup = searchParams.get('cat_group') || '';
  const categoryLabel = searchParams.get('cat_label') || '';
  const region = searchParams.get('region') || '';
  const pickedCodes = useMemo<PickedCode[]>(
    () => readCategorySelection(enstruCode, categoryLabel), [enstruCode, categoryLabel],
  );
  const enstruCodes = useMemo(() => pickedCodes.map((item) => item.code), [pickedCodes]);

  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  const patchParams = useCallback((patch: Record<string, string | null>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  useEffect(() => { getLotRefs().then(setRefs).catch(() => {}); }, []);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setCountSource(null);
    setSearchFailed(false);
    setCursorExpired(false);
    try {
      const data = await searchSuppliers({
        q: q || undefined,
        enstru_code: enstruCodes.length ? enstruCodes : undefined,
        kpved_section: !enstruCodes.length && kpvedSection ? kpvedSection : undefined,
        group: !enstruCodes.length && kpvedSection ? categoryGroup(catGroup) : undefined,
        region: region || undefined,
        skip: page * PAGE_SIZE,
        cursor,
        limit: PAGE_SIZE,
      }, controller.signal);
      if (controller.signal.aborted) {
        if (page === 0) releaseSearchSession(data.search_session);
        return;
      }
      setItems(data.items);
      if (page === 0) analytics.search('suppliers', data.items.length);
      rememberPage(page, data.next_cursor, data.search_session);
      setCursorMode(data.pagination_mode === 'cursor');
      setCountSource(data);
      setHasMore(data.has_more ?? data.items.length === PAGE_SIZE);
      setSortDegraded(!!data.sort_degraded);
      setCategoryPartial(!!data.category_partial);
      setLoading(false);
    } catch (error) {
      if (controller.signal.aborted) return;
      setItems([]);
      setCursorExpired(isSearchCursorError(error));
      setSearchFailed(true);
      setLoading(false);
    }
  }, [q, enstruCodes, kpvedSection, catGroup, region, page, cursor, rememberPage]);

  useEffect(() => { load(); return () => abortRef.current?.abort(); }, [load]);

  const name = (s: SupplierListItem) => (language === 'kk' ? s.nameKz || s.nameRu : s.nameRu || s.nameKz) || s.bin || s.iin || '—';
  const regionName = (s: SupplierListItem) => {
    const code = asCodeList(s.katoList)[0];
    if (!code) return null;
    const r = refs?.regions.find((x) => x.code === code.slice(0, 2));
    return r ? (language === 'kk' ? r.name_kz || r.name_ru : r.name_ru) : null;
  };

  const onPickCategory = (picked: PickedCategory) => {
    setPickerOpen(false);
    patchParams({
      enstru: picked.code || null,
      section: picked.section || null,
      cat_group: picked.group || null,
      cat_label: picked.label,
    });
  };

  const onApplyCategories = (codes: PickedCode[]) => {
    setPickerOpen(false);
    patchParams(categorySelectionParams(codes));
  };

  const categoryBtnText = enstruCodes.length > 1
    ? `${t('lots.categoryBtn')}: ${enstruCodes.length}`
    : (pickedCodes[0]?.label || (kpvedSection ? categoryLabel || kpvedSection : '') || t('lots.categoryBtn'));
  const categoryActive = enstruCodes.length > 0 || !!kpvedSection;

  const hasFilters = !!(q || qInput || enstruCodes.length || kpvedSection || region);
  const resetAll = () => {
    setQInput('');
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const MAX_PAGES = Math.floor(1000000 / PAGE_SIZE) + 1;

  const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-goszakup-500/40';
  const labelCls = 'block text-xs font-bold text-gray-500 mb-1.5';

  return (
    <div className="workspace-page">
      <header className="workspace-header">
        <div>
          <h1 className="workspace-title">{t('suppliers.title')}</h1>
          <p className="workspace-subtitle">{t('suppliers.subtitle')}</p>
        </div>
      </header>
      {access?.trial?.status === 'active' && <div className="mb-6"><ProcurementTrialPanel compact /></div>}

      <div>
        {/* Панель поиска */}
        <div className="workspace-card mb-6">
          <form
            aria-label={t('suppliers.title')}
            className="grid grid-cols-1 gap-3 mb-4 xl:grid-cols-[minmax(0,1fr)_auto]"
            onSubmit={(e) => { e.preventDefault(); patchParams({ q: qInput.trim() || null }); }}
          >
            <SupplierCompanySearch value={qInput} onChange={setQInput} />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start xl:pt-7">
            <EnstruCategoryButton
              active={categoryActive}
              open={pickerOpen}
              label={categoryBtnText}
              onClick={() => setPickerOpen(true)}
            />
            <button
              type="submit"
              className="inline-flex min-h-12 items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-goszakup-600 text-white hover:bg-goszakup-700 transition-colors"
            >
              <Search className="w-4 h-4" /> {t('lots.searchBtn')}
            </button>
            </div>
          </form>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('lots.filters.region')}</label>
              <select value={region} onChange={(e) => patchParams({ region: e.target.value || null })} className={inputCls}>
                <option value="">{t('lots.filters.allRegions')}</option>
                {refs?.regions.map((r) => (
                  <option key={r.code} value={r.code}>{language === 'kk' ? r.name_kz || r.name_ru : r.name_ru}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={resetAll}
                disabled={!hasFilters}
                className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4" /> {t('lots.filters.reset')}
              </button>
            </div>
          </div>
          {categoryActive && (
            <div className="flex flex-wrap gap-2 mt-4">
              {pickedCodes.map((item) => (
                <span key={item.code} title={item.label} className="inline-flex items-center gap-1.5 max-w-[320px] pl-3 pr-2 py-1.5 rounded-full bg-goszakup-50 border border-goszakup-200 text-xs font-semibold text-goszakup-700">
                  <span className="truncate">{item.label}</span>
                  <button type="button" aria-label={`${t('lots.picker.remove')}: ${item.label}`} onClick={() => onApplyCategories(pickedCodes.filter((picked) => picked.code !== item.code))} className="p-0.5 rounded-full hover:bg-goszakup-100">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              {!enstruCodes.length && <span className="text-xs text-goszakup-700 py-1.5">{categoryBtnText}</span>}
              <button type="button" onClick={() => onApplyCategories([])} className="px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-goszakup-700">{t('lots.picker.clear')}</button>
            </div>
          )}
        </div>

        {/* Итоги */}
        {!loading && !searchFailed && (
          <SearchCountLabel label={t('suppliers.found')} {...count}
            retry={count.canRetry ? count.retry : load} canRetry={count.canRetry || count.status === 'error'} />
        )}
        {!loading && !searchFailed && categoryActive && (
          <p className="mb-4 text-xs text-gray-500">{t('lots.supplierCoverage')}</p>
        )}
        {!loading && !searchFailed && (sortDegraded || categoryPartial) && (
          <p role="status" className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {categoryPartial ? t('lots.supplierPartial') : t('lots.supplierSortDegraded')}
          </p>
        )}

        {/* Результаты */}
        {loading ? (
          <SearchProgress active label={t('suppliers.searching')} />
        ) : searchFailed ? (
          <div className="text-center py-24 text-gray-400">
            <SlidersHorizontal className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">{t(cursorExpired ? 'lots.cursorExpired' : 'lots.searchFailed')}</p>
            <button type="button" onClick={() => cursorExpired ? patchParams({ page: null }) : load()}
              className="mt-3 text-sm font-semibold text-goszakup-700 underline">{t(cursorExpired ? 'lots.restartSearch' : 'lots.retry')}</button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <Building2 className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">{t('suppliers.empty')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-[11px] font-bold text-gray-500 uppercase tracking-wide bg-slate-50 border-b border-gray-100">
                    <th className="px-4 py-3 w-[45%]">{t('suppliers.table.name')}</th>
                    <th className="px-4 py-3">{t('suppliers.table.bin')}</th>
                    <th className="px-4 py-3">{t('suppliers.table.region')}</th>
                    <th className="px-4 py-3">{t('suppliers.table.regdate')}</th>
                    <th className="px-4 py-3">{t('suppliers.table.marks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr
                      key={s.pid}
                      onClick={() => { const id = s.bin || s.iin; if (id) navigate(`/suppliers/${id}`); }}
                      className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-goszakup-50/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="font-semibold text-goszakup-700 line-clamp-2">{name(s)}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{s.bin || s.iin || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{regionName(s) || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(s.regdate)}</td>
                      <td className="px-4 py-3">
                        <span className="flex flex-wrap gap-1">
                          {s.in_rnu && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                              <ShieldAlert className="w-3 h-3" /> {t('lots.detail.inRnu')}
                            </span>
                          )}
                          {!!s.markSmallEmployer && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                              {t('suppliers.markSmall')}
                            </span>
                          )}
                          {!!s.qvazi && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                              {t('suppliers.markQvazi')}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Пагинация */}
        {!loading && (!searchFailed || page > 0) && (
          <SearchPagination page={page} pageSize={PAGE_SIZE} total={total}
            maxPages={cursorMode ? Number.POSITIVE_INFINITY : MAX_PAGES} cursorMode={cursorMode}
            hasMore={!searchFailed && hasMore} onPageChange={(next) => {
            patchParams({ page: String(next) }, false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} />
        )}
      </div>

      <EnstruCategoryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPickCategory}
        selected={pickedCodes}
        onApply={onApplyCategories}
      />
    </div>
  );
}
